<script setup lang="ts">
    import { BaseButton } from '@/components/ui/buttons';
import { profileService } from '@/services';
import { useAuthStore } from '@/store/auth';
import { useUserStore } from '@/store/user';
import type { ProfileData } from '@/types/profile';
import { IonContent, IonIcon, IonImg, IonMenu, menuController } from '@ionic/vue';
import { peopleOutline, personCircleOutline, settingsOutline } from 'ionicons/icons';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useBrowser } from '@/composables/useBrowser';
import { cmsService } from '@/services/CmsService';

    const router = useRouter();
    const route = useRoute();
    const userStore = useUserStore();
    const authStore = useAuthStore();
    const { openUrl } = useBrowser();
    const joinCommunityLink = ref<string | null>(null);

    // Profile data from API (same as ProfileIndexPage)
    const profileData = ref<ProfileData | null>(null);

    // Local reactive state for immediate UI updates
    const displayUserName = ref(
        authStore.user?.firstName && authStore.user?.lastName
            ? `${authStore.user.firstName} ${authStore.user.lastName}`
            : authStore.user?.firstName || 'Guest',
    );

    // Watch for user changes and update immediately
    watch(
        () => authStore.user,
        newUser => {
            displayUserName.value =
                newUser?.firstName && newUser?.lastName
                    ? `${newUser.firstName} ${newUser.lastName}`
                    : newUser?.firstName || 'Guest';
        },
        { immediate: true, deep: true },
    );

    // Computed property as fallback
    const userName = computed(() => displayUserName.value);

    // Computed property to check if user is a member (has loyaltyMemberId)
    const isMember = computed(() => {
        return !!authStore.user?.loyaltyMemberId;
    });

    // Computed property for user profile picture - uses same source as ProfileIndexPage
    const userProfilePicture = computed(() => {
        return profileData.value?.profilePicture || profileData.value?.image || null;
    });

    // Fetch profile data (same as ProfileIndexPage)
    const fetchProfileData = async () => {
        // Skip during registration/verification - partial auth tokens
        // don't have profile access (matches mobile app pattern)
        if (!authStore.isAuthenticated) return;
        if (authStore.isInVerificationProcess) return;

        try {
            const response = await profileService.getProfileEdit();
            profileData.value = response.data;
        } catch (error) {
            console.error('Failed to fetch profile data:', error);
        }
    };

    // React to auth state changes (matches mobile app pattern)
    watch(
        () => authStore.isAuthenticated,
        isAuthenticated => {
            if (isAuthenticated) {
                fetchProfileData();
            } else {
                profileData.value = null;
            }
        },
        { immediate: true },
    );

    onMounted(async () => {
        // Load the Join Community link from the CMS (slug: join_link)
        try {
            const page = await cmsService.getPage('join_link');
            joinCommunityLink.value = page.content?.section1_link ?? null;
        } catch {
            // Leave the link empty; the menu item becomes a no-op when unavailable.
            joinCommunityLink.value = null;
        }
    });

    interface MenuItem {
        label: string;
        image?: string;
        icon?: unknown;
        route?: string;
        external?: boolean;
    }

    const menuItems: MenuItem[] = [
        {
            label: 'Dashboard',
            image: '/assets/images/icons/chart.png',
            route: '/home',
        },
        {
            label: 'Loans',
            image: '/assets/images/icons/money.png',
            route: '/home/loans',
        },
        {
            label: 'Rewards',
            image: '/assets/images/icons/gift.png',
            route: '/rewards',
        },
        {
            label: 'Refer & Earn',
            image: '/assets/images/icons/link.png',
            route: '/home/refer-and-earn',
        },
        {
            label: 'My Motorcycle',
            image: '/assets/images/icons/motor.png',
            route: '/motorcycles/my-motorcycles',
        },
        {
            label: 'Transactions',
            image: '/assets/images/icons/file.png',
            route: '/home/transactions',
        },
        {
            label: 'Promos & Advisories',
            image: '/assets/images/icons/megaphone.png',
            route: '/home/promos',
        },
        {
            label: 'Browse Motorcycle',
            image: '/assets/images/icons/motor.png',
            route: '/motorcycles',
        },
        {
            label: 'Loan Calculator',
            image: '/assets/images/icons/calculator.png',
            route: '/home/calculator',
        },
        {
            label: 'Branch Locator',
            image: '/assets/images/icons/map.png',
            route: '/home/branch-locator',
        },
        {
            label: 'Join Kaibigan Community',
            icon: peopleOutline,
            external: true,
        },
    ];

    const navigateTo = async (item: MenuItem) => {
        if (item.external) {
            if (joinCommunityLink.value) {
                await openUrl(joinCommunityLink.value);
                menuController.close('desktop-menu');
            }
            // No-op if the CMS link isn't loaded yet.
            return;
        }

        if (item.route) {
            router.push(item.route);
            menuController.close('desktop-menu');
        }
    };

    const navigateToProfile = () => {
        router.push('/home/profile');
        menuController.close('desktop-menu');
    };

    const isActive = (path: string) => {
        return route.path === path;
    };
</script>

<template>
    <ion-menu
        menu-id="desktop-menu"
        content-id="main-content"
        class="mobile-menu mt-[78px] max-w-56"
        :swipe-gesture="false"
    >
        <ion-content class="ion-padding bg-white">
            <div class="flex h-full flex-col items-center">
                <!-- Profile Section -->
                <div class="relative flex w-full flex-col items-center gap-2.5 rounded-lg p-6">
                    <!-- Settings Icon -->
                    <div class="absolute top-4 right-4">
                        <base-button
                            fill="clear"
                            size="small"
                            class="settings-button"
                            @click="navigateToProfile"
                        >
                            <ion-icon
                                :icon="settingsOutline"
                                class="text-base text-black"
                            />
                        </base-button>
                    </div>

                    <!-- Avatar -->
                    <div
                        class="relative h-14 w-14 overflow-hidden rounded-full border border-gray-200"
                    >
                        <div class="flex h-full w-full items-center justify-center bg-gray-100">
                            <ion-img
                                v-if="userProfilePicture"
                                :src="userProfilePicture"
                                class="h-full w-full object-cover"
                            />
                            <ion-icon
                                v-else
                                :icon="personCircleOutline"
                                class="text-4xl text-gray-400"
                            />
                        </div>
                    </div>

                    <!-- User Info -->
                    <div class="flex flex-col items-center gap-1">
                        <span class="text-base font-bold text-black">{{ userName }}</span>

                        <!-- Member Badge -->
                        <div
                            class="flex items-center justify-center rounded-[14px] bg-orange-100 px-2 py-1"
                        >
                            <span class="text-2xs text-primary">{{
                                isMember ? 'Member' : 'Non-Member'
                            }}</span>
                        </div>

                        <!-- Loyalty Member ID -->
                        <span
                            v-if="authStore.user?.loyaltyMemberId"
                            class="text-2xs text-black"
                            >{{ authStore.user.loyaltyMemberId }}</span
                        >
                    </div>
                </div>

                <!-- Menu Header -->
                <div class="w-full py-2">
                    <span class="text-xs text-black">Menu</span>
                </div>

                <!-- Menu Items -->
                <div class="flex w-full flex-col gap-2">
                    <div
                        v-for="(item, index) in menuItems"
                        :key="index"
                        class="flex cursor-pointer items-center gap-3 rounded-lg p-3 transition-colors"
                        :class="
                            isActive(item.route)
                                ? 'bg-orange-50 text-primary'
                                : 'text-black hover:bg-gray-50'
                        "
                        @click="navigateTo(item)"
                    >
                        <div class="flex items-center justify-center">
                            <ion-img
                                v-if="item.image"
                                :src="item.image"
                                class="h-5 w-5 object-contain"
                            />
                            <ion-icon
                                v-else
                                :icon="item.icon"
                                class="text-xl"
                                :class="isActive(item.route) ? 'text-primary' : 'text-black'"
                            />
                        </div>
                        <span class="text-sm font-medium">
                            {{ item.label }}
                        </span>
                    </div>
                </div>
            </div>
        </ion-content>
    </ion-menu>
</template>

<style scoped>
    ion-menu::part(backdrop) {
        background-color: rgba(0, 0, 0, 0.5);
    }

    ion-menu::part(container) {
        box-shadow: -4px 0 16px rgba(0, 0, 0, 0.1);
    }

    .settings-button {
        --padding-start: 4px;
        --padding-end: 4px;
        --padding-top: 4px;
        --padding-bottom: 4px;
        min-height: auto;
        margin: 0;
    }
</style>
